import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { ToolCard } from './ToolCard';
import { ToolSearch } from './ToolSearch';
import { Tool, ToolCategory } from '../../../../types/navigation';

interface MobileToolsGridProps {
  tools: Tool[];
  categories: ToolCategory[];
  selectedCategory?: string;
  searchQuery?: string;
  onToolSelect: (toolId: string) => void;
  onSearchChange?: (query: string) => void;
  className?: string;
}

/**
 * Mobile-optimized grid layout for dance studio tools
 * Features responsive design, category filtering, and search integration
 */
export const MobileToolsGrid: React.FC<MobileToolsGridProps> = ({
  tools,
  categories,
  selectedCategory,
  searchQuery = '',
  onToolSelect,
  onSearchChange,
  className,
}) => {
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(categories.map(cat => cat.id))
  );

  // Determine grid columns based on screen orientation
  const gridColumns = isLandscape ? 3 : 2;

  // Filter tools based on search query and selected category
  const filteredTools = useMemo(() => {
    let filtered = tools;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        tool =>
          tool.name.toLowerCase().includes(query) ||
          tool.description.toLowerCase().includes(query)
      );
    }

    // Filter by selected category
    if (selectedCategory && selectedCategory !== 'all') {
      filtered = filtered.filter(tool => tool.categoryId === selectedCategory);
    }

    return filtered;
  }, [tools, searchQuery, selectedCategory]);

  // Group tools by category
  const toolsByCategory = useMemo(() => {
    const grouped = new Map<string, Tool[]>();

    categories.forEach(category => {
      const categoryTools = filteredTools.filter(
        tool => tool.categoryId === category.id
      );
      if (categoryTools.length > 0) {
        grouped.set(category.id, categoryTools);
      }
    });

    return grouped;
  }, [filteredTools, categories]);

  const handleCategoryToggle = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleToolSelect = (toolId: string) => {
    const tool = tools.find(t => t.id === toolId);
    if (tool && tool.status === 'available') {
      onToolSelect(toolId);
    }
  };

  // If search query exists, show flat grid without categories
  if (searchQuery.trim()) {
    return (
      <Box className={className} sx={{ p: 2 }}>
        <ToolSearch
          searchQuery={searchQuery}
          onSearchChange={onSearchChange || (() => {})}
          placeholder="Search dance studio tools..."
        />

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 2, mb: 1 }}
        >
          {filteredTools.length} tool{filteredTools.length !== 1 ? 's' : ''}{' '}
          found
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
            },
            gap: 1.5,
          }}
        >
          {filteredTools.map(tool => (
            <ToolCard
              key={tool.id}
              tool={tool}
              onSelect={handleToolSelect}
              variant="compact"
              showDescription={false}
            />
          ))}
        </Box>

        {filteredTools.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: 'text.secondary',
            }}
          >
            <Typography variant="body1">
              No tools found matching &quot;{searchQuery}&quot;
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Try adjusting your search terms
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box className={className} sx={{ p: 2 }}>
      <ToolSearch
        searchQuery={searchQuery}
        onSearchChange={onSearchChange || (() => {})}
        placeholder="Search dance studio tools..."
      />

      {/* Category-based tool display */}
      {categories.map(category => {
        const categoryTools = toolsByCategory.get(category.id) || [];
        const isExpanded = expandedCategories.has(category.id);
        const CategoryIcon = category.icon;

        if (categoryTools.length === 0) return null;

        return (
          <Box
            key={category.id}
            sx={{
              mb: 3,
              border: '2px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: 1,
            }}
          >
            {/* Category Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: 1.5,
                px: 2,
                cursor: 'pointer',
                borderLeft: category.color
                  ? `4px solid ${category.color}`
                  : 'none',
                backgroundColor: category.color
                  ? `${category.color}08`
                  : 'transparent',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: category.color
                    ? `${category.color}15`
                    : 'action.hover',
                  transform: 'translateX(2px)',
                },
              }}
              onClick={() => handleCategoryToggle(category.id)}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: category.color || 'primary.main',
                  mr: 1.5,
                  fontSize: '1.5rem',
                }}
              >
                {CategoryIcon}
              </Box>

              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  flex: 1,
                  color: 'text.primary',
                  fontSize: '1rem',
                }}
              >
                {category.name}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: category.color || 'text.secondary',
                  mr: 1,
                  fontWeight: 600,
                  backgroundColor: category.color
                    ? `${category.color}20`
                    : 'transparent',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                {categoryTools.length}
              </Typography>

              <IconButton
                size="small"
                sx={{
                  color: category.color || 'text.secondary',
                  minWidth: 44,
                  minHeight: 44,
                }}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            {/* Category Tools Grid */}
            <Collapse in={isExpanded} timeout={300}>
              <Box sx={{ p: 2, pt: 1 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
                    gap: 1.5,
                  }}
                >
                  {categoryTools.map(tool => (
                    <ToolCard
                      key={tool.id}
                      tool={tool}
                      onSelect={handleToolSelect}
                      variant="default"
                      showDescription
                      showStatus
                    />
                  ))}
                </Box>
              </Box>
            </Collapse>
          </Box>
        );
      })}

      {/* Empty State */}
      {toolsByCategory.size === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            color: 'text.secondary',
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            No Tools Available
          </Typography>
          <Typography variant="body2">
            Tools will appear here as they become available
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MobileToolsGrid;
